import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

// ── constants ────────────────────────────────────────────────────────────────
const PERIODS = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'This Week'],
  ['month', 'This Month'], ['last_month', 'Last Month'], ['3months', 'Last 3 Months'],
  ['fy', 'FY (Apr–Mar)'],
];
const SCOPES = [['assigned', 'Assigned to'], ['raised', 'Raised by'], ['both', 'Both']];
const STATUS = [
  ['open', 'Open', '#8B5CF6', '#EDE9FE'],
  ['in_progress', 'In Progress', '#D97706', '#FEF3C7'],
  ['resolved', 'Resolved', '#10B981', '#D1FAE5'],
  ['closed', 'Closed', '#64748B', '#F1F5F9'],
];
const ST_LABEL = Object.fromEntries(STATUS.map(s => [s[0], s[1]]));
const ST_STYLE = Object.fromEntries(STATUS.map(s => [s[0], [s[2], s[3]]]));
const PR_STYLE = {
  critical: ['#991b1b', '#fee2e2'], high: ['#c2410c', '#ffedd5'],
  medium: ['#b45309', '#fef3c7'], low: ['#166534', '#f0fdf4'],
};
const initials = (n) => (n || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso.replace(' ', 'T'));
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
const scopeWord = (s) => ({ assigned: 'assigned to', raised: 'raised by', both: 'raised by / assigned to' }[s]);

// ── small UI bits ─────────────────────────────────────────────────────────────
function Avatar({ name, teal }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
      background: teal ? 'linear-gradient(135deg,#1C7293,#2a8fb0)' : 'linear-gradient(135deg,#0B6623,#19a843)',
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 800,
    }}>{initials(name)}</div>
  );
}

function BarWidget({ rows }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(r => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
              <span>{r.label}</span><span>{r.value}</span>
            </div>
            <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 6, height: 20, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(pct, r.value ? 6 : 0)}%`, height: '100%', background: r.color, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', transition: 'width .4s' }}>
                {r.value > 0 && <span style={{ color: '#fff', fontSize: 11, fontWeight: 800, paddingRight: 6 }}>{r.value}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Donut({ summary }) {
  const total = summary.total || 1;
  let off = 0;
  const segs = STATUS.map(s => ({ key: s[0], color: s[2], v: summary[s[0]] || 0 })).filter(x => x.v > 0);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
      <svg width="150" height="150" viewBox="0 0 42 42">
        <circle cx="21" cy="21" r="15.9" fill="none" stroke="var(--n-100)" strokeWidth="5" />
        {segs.map(x => {
          const pct = (x.v / total) * 100;
          const c = <circle key={x.key} cx="21" cy="21" r="15.9" fill="none" stroke={x.color} strokeWidth="5" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={25 - off} />;
          off += pct; return c;
        })}
        <text x="21" y="20" textAnchor="middle" fontSize="7" fontWeight="800" fill="var(--text-primary)">{summary.total || 0}</text>
        <text x="21" y="26" textAnchor="middle" fontSize="3.4" fill="var(--text-secondary)">TOTAL</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 150 }}>
        {STATUS.map(s => (
          <div key={s[0]} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 13 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: s[2], flexShrink: 0 }} />
            {s[1]}<b style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{summary[s[0]] || 0}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── page ───────────────────────────────────────────────────────────────────────
export default function AgentTickets() {
  const nav = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/manager') ? '/manager/tickets' : '/admin/tickets';
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState('all');
  const [period, setPeriod] = useState('month');
  const [scope, setScope] = useState('assigned');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [bucket, setBucket] = useState('total'); // total | open | resolved | closed

  useEffect(() => { apiFetch('/users/agents').then(a => setAgents(a || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/analytics/agent-tickets?period=${period}&scope=${scope}&agent=${encodeURIComponent(agent)}`)
      .then(d => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period, scope, agent]);
  useEffect(() => { load(); }, [load]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };

  const summary = data?.summary || { total: 0, open: 0, in_progress: 0, resolved: 0, closed: 0 };
  const selAgent = agent !== 'all' ? agents.find(a => a.id === agent) : null;
  const totalLab = scope === 'raised' ? 'Total Raised' : scope === 'assigned' ? 'Total Assigned' : 'Total (Raised + Assigned)';
  const openN = (summary.open || 0) + (summary.in_progress || 0);

  const cards = [
    ['total', totalLab, summary.total, 'var(--kg-700)', 'in selected period'],
    ['open', 'Open', openN, '#8B5CF6', 'open + in progress'],
    ['resolved', 'Resolved', summary.resolved, '#10B981', 'fixed, pending close'],
    ['closed', 'Closed', summary.closed, '#64748B', 'completed & closed'],
  ];
  const BUCKET_STATUSES = { total: null, open: ['open', 'in_progress'], resolved: ['resolved'], closed: ['closed'] };
  const bucketLabel = { total: 'all', open: 'open', resolved: 'resolved', closed: 'closed' };

  // bar rows
  let barRows = [];
  let barTitle = '';
  if (selAgent) {
    barTitle = `Status Split — ${selAgent.name}`;
    barRows = STATUS.map(s => ({ label: s[1], value: summary[s[0]] || 0, color: s[2] }));
  } else {
    barTitle = `Tickets by Agent (${scopeWord(scope)})`;
    barRows = (data?.byAgent || [])
      .map(a => ({ label: a.name, value: scope === 'raised' ? a.raised : scope === 'assigned' ? a.assigned : a.both, color: '#0B6623' }))
      .sort((a, b) => b.value - a.value);
  }

  // table rows
  const bucketStatuses = BUCKET_STATUSES[bucket];
  let rows = (data?.tickets || []).filter(t => {
    if (bucketStatuses && !bucketStatuses.includes(t.status)) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (t.ticket_number || '').toLowerCase().includes(s)
      || (t.title || '').toLowerCase().includes(s)
      || (t.raised_by_name || '').toLowerCase().includes(s)
      || (t.assigned_to_name || '').toLowerCase().includes(s);
  });
  rows = [...rows].sort((a, b) => {
    let x = a[sortKey], y = b[sortKey];
    if (x == null) x = ''; if (y == null) y = '';
    if (x < y) return sortDir === 'asc' ? -1 : 1;
    if (x > y) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const th = (key, label, extra = {}) => (
    <th onClick={() => toggleSort(key)} style={{
      padding: '11px 14px', fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)',
      textAlign: extra.right ? 'right' : 'left', textTransform: 'uppercase', letterSpacing: '.5px',
      background: 'var(--n-50)', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
    }}>
      {label} <span style={{ opacity: sortKey === key ? 1 : 0.35, fontSize: 10 }}>{sortKey === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
    </th>
  );

  return (
    <div className="page" style={{ padding: '26px 30px', maxWidth: 1240, margin: '0 auto' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div>
          <div className="page-title">Agent Ticket Dashboard</div>
          <div className="page-sub">Tickets raised by and assigned to each agent — volumes, status split and per-agent breakdown</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 200 }}>
            <select className="input" value={agent} onChange={e => setAgent(e.target.value)}>
              <option value="all">👥 All Agents</option>
              {agents.map(a => <option key={a.id} value={a.id}>👤 {a.name}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 160 }}>
            <select className="input" value={period} onChange={e => setPeriod(e.target.value)}>
              {PERIODS.map(p => <option key={p[0]} value={p[0]}>{p[1]}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* scope toggle */}
      <div style={{ display: 'inline-flex', background: 'var(--n-100)', borderRadius: 10, padding: 3, gap: 2, marginBottom: 18 }}>
        {SCOPES.map(s => {
          const on = scope === s[0];
          return (
            <button key={s[0]} onClick={() => setScope(s[0])} style={{
              border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '7px 18px', borderRadius: 8,
              background: on ? 'var(--bg-surface)' : 'transparent', color: on ? 'var(--kg-700)' : 'var(--text-secondary)',
              boxShadow: on ? '0 1px 2px rgba(15,23,42,.12)' : 'none', transition: '.15s',
            }}>{s[1]}</button>
          );
        })}
      </div>

      {/* selected-agent banner */}
      {selAgent && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', background: 'var(--kg-50)', border: '1px solid var(--kg-200)', borderRadius: 12, marginBottom: 18 }}>
          <div style={{ width: 38, height: 38 }}><Avatar name={selAgent.name} /></div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{selAgent.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{summary.total} tickets {scopeWord(scope)} this agent</div>
          </div>
          <button onClick={() => setAgent('all')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--kg-700)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✕ Clear — show all agents</button>
        </div>
      )}

      {/* stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 18 }}>
        {cards.map(c => {
          const active = bucket === c[0];
          return (
            <div key={c[0]} className="card" onClick={() => setBucket(c[0])} title="Click to filter the tickets below"
              style={{ padding: '18px 20px', position: 'relative', overflow: 'hidden', cursor: 'pointer',
                outline: active ? `2px solid ${c[3]}` : 'none', outlineOffset: -2, transition: '.12s',
                boxShadow: active ? '0 6px 14px -4px rgba(15,23,42,.18)' : undefined }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: c[3] }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{c[1]}</div>
              <div style={{ fontSize: 30, fontWeight: 800, marginTop: 6, letterSpacing: '-1px', color: c[3] }}>{loading ? '…' : (c[2] || 0)}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 4 }}>{c[4]}</div>
            </div>
          );
        })}
      </div>

      {/* charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 18, marginBottom: 18 }}>
        <div className="card" style={{ padding: '20px 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>{barTitle}</div>
          {loading ? <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading…</div>
            : barRows.length ? <BarWidget rows={barRows} />
            : <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No data for this period.</div>}
        </div>
        <div className="card" style={{ padding: '20px 24px' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Status Breakdown</div>
          <Donut summary={summary} />
        </div>
      </div>

      {/* table */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px 12px', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>
              {selAgent ? `${selAgent.name} — ` : ''}{bucket === 'total' ? 'All Tickets' : `${bucketLabel[bucket][0].toUpperCase()}${bucketLabel[bucket].slice(1)} Tickets`}
              <span style={{ color: 'var(--text-secondary)', fontWeight: 600, marginLeft: 6 }}>({rows.length})</span>
            </div>
            {bucket !== 'total' && (
              <button onClick={() => setBucket('total')} style={{ background: 'none', border: 'none', color: 'var(--kg-700)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✕ Clear — show all</button>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }}>🔍</span>
            <input className="input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search ticket / subject / agent…" style={{ paddingLeft: 32, width: 250 }} />
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {th('ticket_number', 'Ticket')}
              {th('title', 'Subject')}
              {th('raised_by_name', 'Raised By')}
              {th('assigned_to_name', 'Assigned To')}
              {th('priority', 'Priority')}
              {th('status', 'Status')}
              {th('created_at', 'Created')}
            </tr></thead>
            <tbody>
              {rows.map(t => {
                const ss = ST_STYLE[t.status] || ['#64748B', '#F1F5F9'];
                const ps = PR_STYLE[t.priority] || ['#64748B', '#F1F5F9'];
                return (
                  <tr key={t.id} onClick={() => nav(`${basePath}/${t.id}`)} title="Open ticket"
                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--n-50)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '12px 14px', fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{t.ticket_number}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>{t.title}</td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>
                      {t.raised_by_name
                        ? <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar name={t.raised_by_name} />{t.raised_by_name}</div>
                        : <span style={{ color: 'var(--text-secondary)' }}>Customer</span>}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>
                      {t.assigned_to_name
                        ? <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar name={t.assigned_to_name} teal />{t.assigned_to_name}</div>
                        : <span style={{ color: 'var(--text-secondary)' }}>Unassigned</span>}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13 }}>
                      <span style={{ color: ps[0], fontWeight: 700 }}>●</span> {t.priority ? t.priority[0].toUpperCase() + t.priority.slice(1) : '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ display: 'inline-block', padding: '3px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: ss[0], background: ss[1] }}>{ST_LABEL[t.status] || t.status}</span>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(t.created_at)}</td>
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 30 }}>No tickets in this period</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
